import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, ChevronRight, ChevronDown, GraduationCap, ScanSearch, Handshake, Plane, Wallet, Layers, ShieldCheck, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { db } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";

import { FlagIcon } from "@/components/FlagIcon";
import { useI18n, LANGUAGES, type Language } from "@/lib/i18n";

export interface NavItem {
  id?: string;
  label: string;
  href: string;
  visible?: boolean;
  children?: NavItem[];
}

const DEFAULT_NAV_ITEMS: NavItem[] = [
  { id: "nav-1", label: "Home", href: "/", visible: true },
  {
    id: "nav-2",
    label: "Who We Are",
    href: "#",
    children: [
      { id: "sub-1", label: "About Us", href: "/about", visible: true },
      { id: "sub-2", label: "Our Team", href: "/team", visible: true },
    ],
  },
  {
    id: "nav-3",
    label: "Services",
    href: "/services",
    children: [
      { id: "sub-3", label: "Football Academy", href: "/services/football-academy" },
      { id: "sub-4", label: "Talent Scouting", href: "/services/talent-scouting" },
      { id: "sub-5", label: "Player Representation", href: "/services/player-representation" },
      { id: "sub-6", label: "International Transfers", href: "/services/international-transfers" },
      { id: "sub-7", label: "Career Investment", href: "/services/career-investment" },
    ],
  },
  { id: "nav-4", label: "Athletes", href: "/athletes" },
  { id: "nav-5", label: "Scouts & Clubs", href: "/scouts" },
  { id: "nav-6", label: "Track Status", href: "/track" },
  { id: "nav-7", label: "Gallery", href: "/gallery" },
  { id: "nav-8", label: "Blog", href: "/blog" },
  { id: "nav-9", label: "Partner", href: "/partner" },
  { id: "nav-10", label: "Contact", href: "/contact" },
];

function normalizeNavItems(raw: any): NavItem[] {
  if (!raw) return DEFAULT_NAV_ITEMS;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) {
      return parsed.map((item: any, idx: number) => ({
        id: item.id || `nav-${idx}`,
        label: item.label || "Menu Item",
        href: item.href || "#",
        visible: item.visible === false ? false : true,
        children: Array.isArray(item.children) && item.children.length > 0
          ? item.children.map((child: any, cIdx: number) => ({
              id: child.id || `sub-${idx}-${cIdx}`,
              label: child.label || "Sub Item",
              href: child.href || "#",
              visible: child.visible === false ? false : true,
            }))
          : undefined,
      }));
    }
  } catch {}
  return DEFAULT_NAV_ITEMS;
}

export function Navbar() {
  const { language, setLanguage } = useI18n();
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [openMobileAccordions, setOpenMobileAccordions] = useState<Record<string, boolean>>({});
  const [location] = useLocation();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: siteSettings = {}, isLoading } = useQuery<Record<string, string>>({
    queryKey: ["configs", "generalSettings"],
    queryFn: async () => {
      try {
        const snap = await getDoc(doc(db, "configs", "generalSettings"));
        return snap.exists() ? (snap.data() as Record<string, string>) : {};
      } catch (err) {
        console.error("Failed to load settings from Firestore:", err);
        return {};
      }
    },
    staleTime: 1000 * 60 * 2,
  });

  const navLinks = useMemo(
    () => {
      if (isLoading) return [];
      if (siteSettings && siteSettings.nav_links_order) return normalizeNavItems(siteSettings.nav_links_order);
      return DEFAULT_NAV_ITEMS;
    },
    [siteSettings.nav_links_order, isLoading]
  );

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setOpenDropdownId(null);
  }, [location]);

  const handleDropdownEnter = (id: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdownId(id);
  };

  const handleDropdownLeave = () => {
    closeTimer.current = setTimeout(() => setOpenDropdownId(null), 120);
  };

  const toggleMobileAccordion = (id: string) => {
    setOpenMobileAccordions((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderDesktopLink = (link: NavItem, i: number) => {
    const itemId = link.id || `item-${i}`;
    const hasChildren = link.children && link.children.length > 0;
    const isChildActive = hasChildren && link.children!.some((c) => location === c.href);
    const isActive = location === link.href || isChildActive;
    const isDropdownOpen = openDropdownId === itemId;

    if (hasChildren) {
      return (
        <div
          key={itemId}
          className="relative"
          onMouseEnter={() => handleDropdownEnter(itemId)}
          onMouseLeave={handleDropdownLeave}
        >
          <button
            className={cn(
              "font-display uppercase tracking-widest text-xs font-semibold transition-colors hover:text-yellow-400 relative py-2 flex items-center gap-1",
              isActive ? "text-yellow-400 font-bold" : "text-gray-300"
            )}
          >
            {link.label}
            <ChevronDown
              size={13}
              className={cn("transition-transform duration-200", isDropdownOpen ? "rotate-180 text-yellow-400" : "text-gray-400")}
            />
            {isActive && (
              <motion.div
                layoutId="navbar-indicator"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-400"
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              />
            )}
          </button>

          <AnimatePresence>
            {isDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.97 }}
                transition={{ duration: 0.15 }}
                onMouseEnter={() => handleDropdownEnter(itemId)}
                onMouseLeave={handleDropdownLeave}
                className="absolute top-full left-1/2 -translate-x-1/2 mt-2 min-w-[240px] bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50 p-2"
              >
                <div className="space-y-1">
                  {link.children!.map((child) => (
                    <Link
                      key={child.id || child.href}
                      href={child.href}
                      className={cn(
                        "flex items-center justify-between px-3.5 py-2.5 rounded-xl transition-all group text-xs font-semibold",
                        location === child.href
                          ? "bg-yellow-500/20 text-yellow-400 font-bold"
                          : "hover:bg-white/5 text-gray-300 hover:text-white"
                      )}
                    >
                      <span>{child.label}</span>
                      <ChevronRight
                        size={13}
                        className={cn(
                          "opacity-0 group-hover:opacity-100 transition-opacity",
                          location === child.href ? "opacity-100 text-yellow-400" : "text-gray-400"
                        )}
                      />
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return (
      <Link
        key={itemId}
        href={link.href}
        className={cn(
          "font-display uppercase tracking-widest text-xs font-semibold transition-colors hover:text-yellow-400 relative py-2",
          location === link.href ? "text-yellow-400 font-bold" : "text-gray-300"
        )}
      >
        {link.label}
        {location === link.href && (
          <motion.div
            layoutId="navbar-indicator"
            className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-400"
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          />
        )}
      </Link>
    );
  };

  const renderMobileLink = (link: NavItem, i: number) => {
    const itemId = link.id || `mobile-item-${i}`;
    const hasChildren = link.children && link.children.length > 0;
    const isAccordionOpen = !!openMobileAccordions[itemId];
    const isChildActive = hasChildren && link.children!.some((c) => location === c.href);
    const isActive = location === link.href || isChildActive;

    if (hasChildren) {
      return (
        <div key={itemId} className="border-b border-white/5 py-1">
          <button
            onClick={() => toggleMobileAccordion(itemId)}
            className={cn(
              "flex items-center justify-between font-display uppercase tracking-widest text-base py-2.5 w-full",
              isActive ? "text-yellow-400 font-bold" : "text-gray-300"
            )}
          >
            <span>{link.label}</span>
            <ChevronDown
              size={16}
              className={cn("text-yellow-500/70 transition-transform", isAccordionOpen ? "rotate-180" : "")}
            />
          </button>

          <AnimatePresence>
            {isAccordionOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden pl-4 pb-2 space-y-1"
              >
                {link.children!.map((child) => (
                  <Link
                    key={child.id || child.href}
                    href={child.href}
                    className={cn(
                      "flex items-center justify-between py-2 text-sm font-medium",
                      location === child.href ? "text-yellow-400 font-bold" : "text-gray-400 hover:text-white"
                    )}
                  >
                    <span>{child.label}</span>
                    <ChevronRight size={14} className="text-gray-500" />
                  </Link>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return (
      <Link
        key={itemId}
        href={link.href}
        className={cn(
          "flex items-center justify-between font-display uppercase tracking-widest text-base py-3 border-b border-white/5",
          location === link.href ? "text-yellow-400 font-bold" : "text-gray-300"
        )}
      >
        {link.label}
        <ChevronRight size={16} className="text-yellow-500/50" />
      </Link>
    );
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b",
        isScrolled
          ? "bg-slate-950/90 backdrop-blur-md border-white/10 py-3 shadow-lg"
          : "bg-transparent border-transparent py-5"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <a href={import.meta.env.BASE_URL || "/"} className="flex items-center group">
            <img
              src={`${import.meta.env.BASE_URL}images/logo.png`}
              alt="Eliada Sports Agency Logo"
              className="h-12 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6">
              {navLinks.filter((l) => l.visible !== false).map((link, i) => renderDesktopLink(link, i))}

            {/* Language Switcher Dropdown */}
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-bold transition-colors"
                title="Change Language"
              >
                <FlagIcon
                  country={
                    language === "de"
                      ? "DE"
                      : language === "fr"
                      ? "FR"
                      : language === "es"
                      ? "ES"
                      : "GB"
                  }
                  size="sm"
                />
                <span className="uppercase text-[11px] font-mono">{language}</span>
                <ChevronDown size={12} className={cn("transition-transform", langMenuOpen ? "rotate-180" : "")} />
              </button>

              <AnimatePresence>
                {langMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 6, scale: 0.95 }}
                    className="absolute right-0 mt-2 w-36 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl p-1.5 z-50 space-y-1"
                  >
                    {LANGUAGES.map((opt) => (
                      <button
                        key={opt.code}
                        onClick={() => {
                          setLanguage(opt.code);
                          setLangMenuOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors",
                          language === opt.code
                            ? "bg-yellow-500/20 text-yellow-400 font-bold"
                            : "text-slate-300 hover:bg-white/5 hover:text-white"
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <FlagIcon country={opt.flagCountry} size="sm" />
                          <span>{opt.nativeName}</span>
                        </span>
                        <span className="text-[10px] uppercase font-mono text-slate-500">{opt.code}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              href="/join"
              className="font-display uppercase tracking-widest text-xs font-bold bg-yellow-500 hover:bg-yellow-400 text-black transition-all px-5 py-2.5 rounded-xl shadow-md active:scale-95 shrink-0"
            >
              Apply Now
            </Link>
          </nav>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="lg:hidden text-white hover:text-yellow-400 transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-b border-white/10 overflow-hidden max-h-[85vh] overflow-y-auto"
          >
            <div className="px-4 py-6 flex flex-col space-y-1">
              {navLinks.filter((l) => l.visible !== false).map((link, i) => renderMobileLink(link, i))}

              {/* Mobile Language Selector */}
              <div className="pt-4 flex items-center justify-between border-t border-white/10">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Select Language</span>
                <div className="flex gap-1.5">
                  {LANGUAGES.map((opt) => (
                    <button
                      key={opt.code}
                      onClick={() => setLanguage(opt.code)}
                      className={cn(
                        "p-1.5 rounded-lg border text-xs font-mono uppercase transition-colors",
                        language === opt.code
                          ? "bg-yellow-500/20 border-yellow-500 text-yellow-400 font-bold"
                          : "border-white/10 text-gray-400"
                      )}
                    >
                      <FlagIcon country={opt.flagCountry} size="sm" />
                    </button>
                  ))}
                </div>
              </div>

              <Link
                href="/join"
                className="mt-4 block text-center font-display uppercase tracking-widest text-sm font-bold bg-yellow-500 text-black px-6 py-3 rounded-xl shadow-md"
              >
                Apply Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
